/**
 * POS 連接: a 保雅電腦 POS calls https://posapi.<platform domain>/api/pos/* with the brand's keys (spec:
 * 「保雅電腦積分和優惠券文檔」 1.8). Only native brands (自家資料庫); the platform admin switches it on per brand.
 */
import { z } from 'zod';

/** The eight POS endpoints, as the document names them (under /api/pos/). */
export const POS_ENDPOINTS = ['transaction', 'transaction-cancel', 'member', 'redemption', 'point-burn', 'point-burn-result', 'point-burn-cancel', 'point-earn'] as const;
export type PosEndpoint = (typeof POS_ENDPOINTS)[number];

// ---- platform admin (平台管理後台 › 品牌 › POS 連接)

export interface PosConnectionAdmin {
  enabled: boolean;
  /** brandCode the POS sends; unique across the platform. */
  brandCode: string;
  /** posWebServiceKey: the POS signs every request with it (h). */
  serviceKey: string;
  /** qrcodeEncryptKey (16 characters): encrypts the QR printed on receipts without a member. */
  qrKey: string;
  /** Base address to give the POS vendor, e.g. https://posapi.loyaltyplus.hk/api/pos */
  endpoint: string;
  updatedAt: string;
}

export const posConnectionSchema = z.object({
  enabled: z.boolean(),
  brandCode: z
    .string()
    .trim()
    .min(1)
    .max(32)
    .regex(/^[A-Za-z0-9_-]+$/, 'letters, digits, - and _'),
});
export type PosConnectionInput = z.infer<typeof posConnectionSchema>;

/** New key: the POS stops working until the vendor enters it. */
export const posRotateSchema = z.object({ key: z.enum(['service', 'qr']) });

// ---- brand portal (POS 連接 page)

/** Brand settings section `pos` (網站設定 is not involved; edited on the POS 連接 page). */
export interface PosSettings {
  /** 積分抵扣 is approved without asking the member (otherwise the member confirms on the member site). */
  autoApproveBurn: boolean;
  /** Days a receipt without a member can be claimed by scanning its QR. */
  claimDays: number;
}
export const posSettingsSchema = z.object({ autoApproveBurn: z.boolean(), claimDays: z.number().int().min(1).max(365) });

export interface PosConnectionStatus {
  /** Switched on by the platform. */
  connected: boolean;
  brandCode: string | null;
  endpoint: string;
  lastCallAt: string | null;
}

export interface PosLogRow {
  id: string;
  endpoint: string;
  shopCode: string | null;
  /** Invoice number, member, coupon or check_valid the call was about. */
  ref: string | null;
  /** 0 = success, 1 = failure (the POS's own codes). */
  result: number;
  errCode: number | null;
  message: string | null;
  ms: number;
  createdAt: string;
}

/** earned = points given; pending = no member yet (receipt QR); cancelled = 刪除發票; expired = not claimed in time. */
export type PosInvoiceStatus = 'earned' | 'pending' | 'cancelled' | 'expired';

export interface PosInvoiceRow {
  id: string;
  shopCode: string;
  shopName: string | null;
  invoiceNo: string;
  /** From the POS (Hong Kong time), ISO. */
  transactionAt: string;
  amount: number;
  memberId: string | null;
  memberNo: string | null;
  memberName: string | null;
  points: number | null;
  status: PosInvoiceStatus;
  claimUntil: string | null;
  createdAt: string;
}

/** pending = waiting for the member; approved = points taken; declined by the member; cancelled by the POS (refunded if taken). */
export type PosBurnStatus = 'pending' | 'approved' | 'declined' | 'cancelled';

export interface PosBurnRow {
  id: string;
  shopCode: string;
  shopName: string | null;
  memberId: string;
  memberNo: string | null;
  memberName: string | null;
  points: number;
  status: PosBurnStatus;
  createdAt: string;
  decidedAt: string | null;
}

// ---- member site

/** A POS asks to take points (積分抵扣); the member agrees or declines on the member site. */
export interface MemberPosBurn {
  id: string;
  points: number;
  shopName: string;
  createdAt: string;
  /** After this the POS stops waiting. */
  expiresAt: string;
}

/** GET /api/member/pos: whether the brand's POS is connected, and 積分抵扣 requests waiting for this member. */
export interface MemberPosState {
  enabled: boolean;
  burns: MemberPosBurn[];
}

/** POST /api/member/pos/burns/:id */
export const posBurnAnswerSchema = z.object({ agree: z.boolean() });

/** 掃收據領積分: the QR printed on a receipt without a member. */
export const posClaimSchema = z.object({ code: z.string().trim().min(8).max(400) });

export interface PosClaimResult {
  invoiceNo: string;
  shopName: string;
  amount: number;
  points: number;
  stamps: number;
}

/** 「POS 適用菜式編號」: codes separated by commas (、 and spaces are read as commas). */
export const posItemCodes = z
  .string()
  .max(500)
  .transform((s) =>
    s
      .split(/[,，、\s]+/)
      .map((x) => x.trim())
      .filter(Boolean)
      .join(','),
  )
  .transform((s) => s || null)
  .nullable();
